import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import RtlButton from './RtlButton';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useLocation } from 'react-router';
import dayjs from 'dayjs';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  deleteOne as deleteOrder,
  getOne as getOrder,
  getInstallments,
  getAllInstallments,
  type Order,
  type Installment,
} from '../data/orders';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowForward } from '@mui/icons-material';
import PaymentDialog from './PaymentDialog';
import InstallmentSelectionDialog from './InstallmentSelectionDialog';
import InstallmentPaymentDialog from './InstallmentPaymentDialog';
import InstallmentEditDialog from './InstallmentEditDialog';
import CancelOrderDialog from './CancelOrderDialog';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';

export default function OrderShow() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, direction } = useLanguage();
  const { user } = useAuth();

  const dialogs = useDialogs();
  const notifications = useNotification();

  // cost, full_amount and installment amounts are optional, so the API can
  // return null for any of them. Normalise before doing arithmetic on them.
  const toAmount = (value: string | number | null | undefined): number => {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return typeof numericValue === 'number' && !Number.isNaN(numericValue) ? numericValue : 0;
  };

  const toOptionalAmount = (
    value: string | number | null | undefined,
  ): number | undefined => {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return typeof numericValue === 'number' && !Number.isNaN(numericValue)
      ? numericValue
      : undefined;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CREATED':
        return '#ff9800'; // Yellow/Orange
      case 'BOOKED':
        return '#4caf50'; // Green
      case 'CANCELED':
        return '#f44336'; // Red
      case 'COMPLETED':
        return '#2196f3'; // Blue
      default:
        return '#757575'; // Gray
    }
  };

  const [order, setOrder] = React.useState<Order | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [installments, setInstallments] = React.useState<Installment[]>([]);
  const [payments, setPayments] = React.useState<any[]>([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false);
  const [installmentSelectionDialogOpen, setInstallmentSelectionDialogOpen] = React.useState(false);
  const [selectedInstallmentForPayment, setSelectedInstallmentForPayment] = React.useState<Installment | null>(null);
  const [installmentPaymentDialogOpen, setInstallmentPaymentDialogOpen] = React.useState(false);
  const [selectedInstallmentForEdit, setSelectedInstallmentForEdit] = React.useState<Installment | null>(null);
  const [installmentEditDialogOpen, setInstallmentEditDialogOpen] = React.useState(false);
  const [isCreateInstallment, setIsCreateInstallment] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);

  // Installments and payments can only be added once the order has a cost and a
  // total amount (the total is itself derived from the price per person).
  const hasRequiredAmounts =
    toAmount(order?.cost) > 0 && toAmount(order?.full_amount) > 0;

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getOrder(Number(orderId));
      setOrder(showData);

      // Load installments from order data
      if (showData.installments) {
        setInstallments(showData.installments);
      } else {
        // Fallback: try to load installments separately
        try {
          const installmentsData = await getInstallments(Number(orderId));
          setInstallments(installmentsData);
        } catch (err) {
          console.error('Failed to load installments:', err);
          setInstallments([]);
        }
      }

      // Load payments from order data
      if (showData.payments) {
        setPayments(showData.payments);
      } else {
        setPayments([]);
      }
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [orderId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOrderEdit = React.useCallback(() => {
    navigate(`/orders/${orderId}/edit`);
  }, [navigate, orderId]);

  const handleOrderDelete = React.useCallback(async () => {
    if (!order) {
      return;
    }

    const confirmed = await dialogs.confirm(
      t('messages.deleteConfirm'),
      {
        title: t('orders.delete'),
        severity: 'error',
        okText: t('common.delete'),
        cancelText: t('common.cancel'),
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteOrder(Number(orderId));

        navigate('/orders');

        notifications.show(t('messages.deleteSuccess'), {
          severity: 'success',
          autoHideDuration: 3000,
        });
      } catch (deleteError) {
        notifications.show(
          `${t('messages.errorOccurred')}: ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [order, dialogs, orderId, navigate, notifications, t]);

  const handleBack = React.useCallback(() => {
    const state = location.state as { from?: string; tab?: number } | null;
    if (state?.from) {
      navigate(state.from, { state: { activeTab: state.tab } });
    } else {
      navigate('/orders');
    }
  }, [navigate, location]);

  const handlePayNow = React.useCallback(() => {
    if (!order) return;

    // Check if order is cancelled or completed
    if (order.status === 'CANCELED' || order.status === 'COMPLETED') {
      notifications.show(
        t('orders.cannotPayCancelledOrCompleted'),
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      return;
    }

    // Validate cost and full_amount are set
    const cost = typeof order.cost === 'string' ? parseFloat(order.cost) : order.cost;
    const fullAmount = typeof order.full_amount === 'string' ? parseFloat(order.full_amount) : order.full_amount;

    if (!cost || cost <= 0 || !fullAmount || fullAmount <= 0) {
      notifications.show(
        t('orders.costAndAmountRequired'),
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      return;
    }

    const paidAmount = typeof order.paid_amount === 'string'
      ? parseFloat(order.paid_amount)
      : order.paid_amount || 0;

    if (paidAmount > 0) {
      // Show installment selection dialog
      setInstallmentSelectionDialogOpen(true);
    } else {
      // Show full payment dialog
      setPaymentDialogOpen(true);
    }
  }, [order, notifications, t]);

  const handlePaymentDialogClose = React.useCallback(() => {
    setPaymentDialogOpen(false);
    loadData(); // Refresh the data after payment
  }, [loadData]);

  const handleInstallmentSelectionClose = React.useCallback(() => {
    setInstallmentSelectionDialogOpen(false);
    loadData(); // Refresh the data after payment
  }, [loadData]);

  const handleInstallmentPayment = React.useCallback((installment: Installment) => {
    setSelectedInstallmentForPayment(installment);
    setInstallmentPaymentDialogOpen(true);
  }, []);

  const handleInstallmentPaymentClose = React.useCallback(() => {
    setInstallmentPaymentDialogOpen(false);
    setSelectedInstallmentForPayment(null);
    loadData(); // Refresh the data after payment
  }, [loadData]);

  const handleCancelOrder = React.useCallback(() => {
    setCancelDialogOpen(true);
  }, []);

  const handleCancelDialogClose = React.useCallback(() => {
    setCancelDialogOpen(false);
  }, []);

  const handleCancelSuccess = React.useCallback(() => {
    setCancelDialogOpen(false);
    loadData(); // Refresh the data after cancellation
  }, [loadData]);

  const handleInstallmentEdit = React.useCallback((installment: Installment) => {
    setSelectedInstallmentForEdit(installment);
    setIsCreateInstallment(false);
    setInstallmentEditDialogOpen(true);
  }, []);

  const handleInstallmentEditClose = React.useCallback(() => {
    setInstallmentEditDialogOpen(false);
    setSelectedInstallmentForEdit(null);
  }, []);

  const handleInstallmentEditSuccess = React.useCallback(() => {
    setInstallmentEditDialogOpen(false);
    setSelectedInstallmentForEdit(null);
    loadData(); // Refresh the data after edit/create
  }, [loadData]);

  const handleCreateInstallment = React.useCallback(() => {
    if (!hasRequiredAmounts) {
      notifications.show(t('orders.costAndAmountRequired'), {
        severity: 'error',
        autoHideDuration: 3000,
      });
      return;
    }

    setSelectedInstallmentForEdit(null);
    setIsCreateInstallment(true);
    setInstallmentEditDialogOpen(true);
  }, [hasRequiredAmounts, notifications, t]);

  const handleViewReceipt = React.useCallback(() => {
    if (order?.media && order.media.length > 0) {
      window.open(order.media[0].url, '_blank');
    }
  }, [order]);

  const renderShow = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return order ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.id')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.id}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.customer')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.customer?.name || order.customerName || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.customerNumber')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <span dir="ltr">{(order.customer?.mobile_number || order.customerNumber || 'N/A').replace(/\s+/g, '')}</span>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.createdBy')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.user?.name || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.people_number')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.people_number}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.destination')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.destination || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.cost')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.cost ? `${typeof order.cost === 'string' ? parseFloat(order.cost).toLocaleString() : order.cost.toLocaleString()} ${direction === 'rtl' ? 'د.إ' : 'AED'}` : 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.totalAmount')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.full_amount ? `${typeof order.full_amount === 'string' ? parseFloat(order.full_amount).toLocaleString() : order.full_amount.toLocaleString()} ${direction === 'rtl' ? 'د.إ' : 'AED'}` : 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.paidAmount')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.paid_amount ? `${typeof order.paid_amount === 'string' ? parseFloat(order.paid_amount).toLocaleString() : order.paid_amount.toLocaleString()} ${direction === 'rtl' ? 'د.إ' : 'AED'}` : `0.00 ${direction === 'rtl' ? 'د.إ' : 'AED'}`}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.pendingAmount')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.pending_amount ? `$${typeof order.pending_amount === 'string' ? parseFloat(order.pending_amount).toLocaleString() : order.pending_amount.toLocaleString()} ${direction === 'rtl' ? 'د.إ' : 'AED'}` : `0.00 ${direction === 'rtl' ? 'د.إ' : 'AED'}`}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.status')}</Typography>
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={t(`orders.${order.status.toLowerCase()}`)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(order.status),
                    color: 'white',
                    fontWeight: 600,
                    minWidth: 90,
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          {order.status === 'CANCELED' && order.notes && (
              <Grid size={{ xs: 6 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">{t('orders.notes')}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {order.notes}
                  </Typography>
                </Paper>
              </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.createdAt')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {dayjs(order.created_at).format('YYYY-MM-DD HH:mm')}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">{t('orders.confirmedAt')}</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {order.confirmed_at ? dayjs(order.confirmed_at).format('YYYY-MM-DD HH:mm') : t('orders.notConfirmed')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Installments Section */}
        <Divider sx={{ my: 3 }} />
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('orders.installments')}
            action={
              order && order.status !== 'CANCELED' && order.status !== 'COMPLETED' && hasRequiredAmounts ? (
                <RtlButton
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateInstallment}
                  sx={{
                    background: '#4caf50 !important',
                    boxShadow: 'unset !important',
                    border: '0 !important',
                    '&:hover': { background: '#388e3c !important' },
                  }}
                >
                  {t('orders.addInstallment')}
                </RtlButton>
              ) : null
            }
          />
          <CardContent>
            {installments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('orders.noInstallments')}
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('orders.installment')}</TableCell>
                      <TableCell>{t('orders.dueDate')}</TableCell>
                      <TableCell>{t('orders.amount')}</TableCell>
                      <TableCell>{t('orders.status')}</TableCell>
                      <TableCell>{t('orders.paymentMethod')}</TableCell>
                      <TableCell align="center">{t('orders.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installments.map((installment, index) => {
                      const amount = toAmount(installment.amount);
                      const isPaid = installment.status === 'PAID' || installment.paid === true;
                      return (
                        <TableRow key={installment.id || index}>
                          <TableCell>#{installment.seq || index + 1}</TableCell>
                          <TableCell>{dayjs(installment.due_date).format('YYYY-MM-DD')}</TableCell>
                          <TableCell>{amount.toFixed(2)} {direction === 'rtl' ? 'د.إ' : 'AED'}</TableCell>
                          <TableCell>
                            <Chip
                              label={isPaid ? t('orders.paid') : t('orders.unpaid')}
                              size="small"
                              color={isPaid ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>
                            {installment.payment_method ? t(`orders.${installment.payment_method.toLowerCase()}`) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {!isPaid && (
                                <Tooltip title={t('orders.payInstallment')}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleInstallmentPayment(installment)}
                                  >
                                    <PaymentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {order && order.status !== 'CANCELED' && order.status !== 'COMPLETED' && (
                                <Tooltip title={t('common.edit')}>
                                  <IconButton
                                    size="small"
                                    color="default"
                                    onClick={() => handleInstallmentEdit(installment)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {installment.media && installment.media.length > 0 && (
                                <Tooltip title={t('orders.viewReceipt')}>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => window.open(installment.media![0].url, '_blank')}
                                  >
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Payments Section */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title={t('orders.payments')} />
          <CardContent>
            {payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('orders.noPayments')}
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('orders.amount')}</TableCell>
                      <TableCell>{t('orders.paymentMethod')}</TableCell>
                      <TableCell>{t('orders.date')}</TableCell>
                      <TableCell>{t('orders.notes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment, index) => {
                      const amount = toAmount(payment.amount);
                      return (
                        <TableRow key={payment.id || index}>
                          <TableCell>{amount.toFixed(2)} {direction === 'rtl' ? 'د.إ' : 'AED'}</TableCell>
                          <TableCell>
                            {payment.method ? t(`orders.${payment.method.toLowerCase()}`) : '-'}
                          </TableCell>
                          <TableCell>
                            {payment.paid_at ? dayjs(payment.paid_at).format('YYYY-MM-DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell>{payment.notes || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Stack
            direction="row"
            spacing={1}
            sx={{
              '& > *': {
                marginLeft: direction === 'rtl' ? '0 !important' : undefined,
                marginRight: direction === 'rtl' ? '0 !important' : undefined,
              },
              '& > *:not(:last-child)': {
                [direction === 'rtl' ? 'marginLeft' : 'marginRight']: '8px !important',
              },
            }}
          >
            {order && order.status !== 'CANCELED' && order.status !== 'COMPLETED' && hasRequiredAmounts && (
              <RtlButton
                variant="contained"
                startIcon={<PaymentIcon />}
                onClick={handlePayNow}
                sx={{ background: '#4caf50 !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#388e3c !important' } }}
              >
                {(typeof order.paid_amount === 'string'
                  ? parseFloat(order.paid_amount)
                  : order.paid_amount || 0) > 0
                  ? t('orders.payInstallment')
                  : t('orders.payNow')}
              </RtlButton>
            )}
            {order?.media && order.media.length > 0 && (
              <RtlButton
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={handleViewReceipt}
                sx={{ background: '#9c27b0 !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#7b1fa2 !important' } }}
              >
                {t('orders.viewReceipt')}
              </RtlButton>
            )}
            {((user?.role === 'GM' && order.status !== 'CANCELED' && order.status !== 'COMPLETED') || (user?.role !== 'GM' && order.status === 'CREATED')) && (
            <RtlButton
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleOrderEdit}
              sx={{ background: '#028bcb !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#0277a8 !important' } }}
            >
              {t('common.edit')}
            </RtlButton>
            )}
            {order.status === 'CREATED' && (
              <RtlButton
                variant="contained"
                color="warning"
                startIcon={<CancelIcon />}
                onClick={handleCancelOrder}
                sx={{ background: '#ff9800 !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#f57c00 !important' } }}
              >
                {t('orders.cancelOrder')}
              </RtlButton>
            )}
            {user?.role === 'GM' && (
              <RtlButton
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleOrderDelete}
                disabled={isLoading}
                sx={{ background: '#d32f2f !important', boxShadow: 'unset !important', border: '0 !important', '&:hover': { background: '#c62828 !important' }, '&:disabled': { background: '#ef5350 !important', opacity: 0.6 } }}
              >
                {t('common.delete')}
              </RtlButton>
            )}
          </Stack>
        </Stack>
      </Box>
    ) : null;
  }, [
    isLoading,
    error,
    order,
    handleBack,
    handleOrderEdit,
    handleOrderDelete,
    handleInstallmentPayment,
    handleViewReceipt,
    installments,
    t,
    direction,
    getStatusColor,
    toAmount,
    hasRequiredAmounts,
  ]);

  const isRtl = direction === 'rtl';

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('orders.title'), path: '/orders' },
        { title: t('orders.view') },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : '#31394a',
              color: theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'white'
            },
          })}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>

      {/* Payment Dialog */}
      {order && (
        <PaymentDialog
          open={paymentDialogOpen}
          payload={{
            orderId: order.id,
            totalAmount: typeof order.full_amount === 'string'
              ? parseFloat(order.full_amount)
              : order.full_amount,
            pendingAmount: typeof order.pending_amount === 'string'
              ? parseFloat(order.pending_amount)
              : order.pending_amount,
            orderStatus: order.status,
          }}
          onClose={handlePaymentDialogClose}
        />
      )}

      {/* Installment Selection Dialog */}
      {order && (
        <InstallmentSelectionDialog
          open={installmentSelectionDialogOpen}
          orderId={order.id}
          onClose={handleInstallmentSelectionClose}
          onSuccess={handleInstallmentSelectionClose}
        />
      )}

      {/* Installment Payment Dialog */}
      <InstallmentPaymentDialog
        open={installmentPaymentDialogOpen}
        installment={selectedInstallmentForPayment}
        onClose={handleInstallmentPaymentClose}
        onSuccess={handleInstallmentPaymentClose}
      />

      {/* Cancel Order Dialog */}
      <CancelOrderDialog
        open={cancelDialogOpen}
        orderId={order?.id || null}
        onClose={handleCancelDialogClose}
        onSuccess={handleCancelSuccess}
      />

      {/* Installment Edit/Create Dialog */}
      {order && (
        <InstallmentEditDialog
          open={installmentEditDialogOpen}
          installment={selectedInstallmentForEdit}
          orderId={order.id}
          isEdit={!isCreateInstallment}
          totalAmount={toOptionalAmount(order.full_amount)}
          allInstallments={installments}
          onClose={handleInstallmentEditClose}
          onSuccess={handleInstallmentEditSuccess}
          onDelete={handleInstallmentEditSuccess}
        />
      )}
    </PageContainer>
  );
}

